import { useId, useRef, useState } from "react"
import type { PortalBrandingResponse } from "@ledgerbox/domain"

import { portalHelpRows, type HelpMode } from "./help-copy"
import { PortalForwardingSection } from "./portal-forwarding"
import { ModalDialog } from "./ui"

/**
 * The header's help button and the sheet it opens, in the portal's modal
 * dialog.
 */
export function PortalHelp({
  branding,
  sectionLabel,
  mode,
}: {
  branding: PortalBrandingResponse
  sectionLabel: string
  mode: HelpMode
}) {
  const [open, setOpen] = useState(false)
  const closeButton = useRef<HTMLButtonElement>(null)
  const titleId = useId()
  const sheetId = useId()
  const firmName = branding.displayName
  const rows = portalHelpRows({ firmName, sectionLabel, mode })
  const hasSupport = Boolean(branding.supportEmail || branding.supportPhone)

  return (
    <>
      <button
        type="button"
        className="icon-button help-button"
        aria-label="How this portal works"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={sheetId}
        onClick={() => setOpen(true)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M9.4 9.3a2.7 2.7 0 0 1 5.2 1c0 1.8-2.6 2.5-2.6 2.5" />
          <path d="M12 16.9h.01" />
        </svg>
      </button>
      <ModalDialog
        id={sheetId}
        open={open}
        onClose={() => setOpen(false)}
        labelledBy={titleId}
        className="help-sheet"
        initialFocus={closeButton}
      >
        <div className="help-sheet-panel">
          <div className="help-sheet-head">
            <h2 id={titleId}>How this works</h2>
            <button
              ref={closeButton}
              type="button"
              className="icon-button"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <p className="help-sheet-lede">
            What {firmName} needs from you, and what happens next
          </p>
          <ul className="help-list">
            {rows.map((row) => (
              <li key={row.title}>
                <h3>{row.title}</h3>
                <p>{row.body}</p>
              </li>
            ))}
          </ul>
          <PortalForwardingSection open={open} firmName={firmName} />
          {hasSupport ? (
            <p className="help-support">
              <span>
                Need help? Contact {branding.supportName || firmName}.
              </span>
              {branding.supportEmail ? (
                <a href={`mailto:${branding.supportEmail}`}>
                  {branding.supportEmail}
                </a>
              ) : null}
              {branding.supportPhone ? (
                <a href={`tel:${branding.supportPhone}`}>
                  {branding.supportPhone}
                </a>
              ) : null}
            </p>
          ) : null}
        </div>
      </ModalDialog>
    </>
  )
}
